import { FC } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
} from 'react-router-dom';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { RoleGateway } from 'src/components/role-gateway';
import { PageSpinner } from 'src/components/spinner-container';
import { useTso } from 'src/pages/market-information/players/operators-detail/use-tso';
import { ApplyForAssignment } from 'src/pages/operators/assignments/apply-for-assignment';
import { AssigmentDetails } from 'src/pages/operators/assignments/details';
import { ContractDetails } from 'src/pages/operators/assignments/details/contract-details';
import { FinancialSecurityAssociateForm } from 'src/pages/operators/assignments/details/financial-securities/associate';
import { FinancialSecurityDetails } from 'src/pages/operators/assignments/details/financial-securities/details';
import { RequestRelease } from 'src/pages/operators/assignments/details/financial-securities/details/request-release';
import { SubmitFinancialSecurity } from 'src/pages/operators/assignments/details/financial-securities/submit';
import { AssignmentsOverview } from 'src/pages/operators/assignments/overview';
import { RequestFmct } from 'src/pages/operators/assignments/request-fmct';
import { useShipperUserData } from 'src/pages/shipper-admin/details/use-shipper-user-data';

export const Assignments: FC = () => {
  return (
    <RoleGateway allowed={['SHIPPER_USER', 'SHIPPER_ADMIN']}>
      <Switch>
        <Route path="/operators/assignments/overview">
          <AssignmentsOverview />
        </Route>

        <Route path="/operators/assignments" exact>
          <Redirect to="/operators/assignments/overview" />
        </Route>

        <Route path="/dashboard/operators/assignments/:operatorUuid/request-fmct">
          <RequestFmct />
        </Route>

        <Route path="/dashboard/operators/assignments/:operatorUuid/financial-securities/cash-deposit/:financialSecurityId/associate">
          <FinancialSecurityAssociateForm type="CASH_DEPOSIT" />
        </Route>
        <Route path="/dashboard/operators/assignments/:operatorUuid/financial-securities/rating/:financialSecurityId/associate">
          <FinancialSecurityAssociateForm type="RATING" />
        </Route>
        <Route path="/dashboard/operators/assignments/:operatorUuid/financial-securities/bank-guarantee/:financialSecurityId/associate">
          <FinancialSecurityAssociateForm type="BANK_GUARANTEE" />
        </Route>
        <Route path="/dashboard/operators/assignments/:operatorUuid/financial-securities/parent-company-guarantee/:financialSecurityId/associate">
          <FinancialSecurityAssociateForm type="PARENT_COMPANY_GUARANTEE" />
        </Route>

        <Route
          path="/dashboard/operators/assignments/:operatorUuid/financial-securities/cash-deposit/:financialSecurityId"
          exact
        >
          <FinancialSecurityDetails type="CASH_DEPOSIT" />
        </Route>
        <Route
          path="/dashboard/operators/assignments/:operatorUuid/financial-securities/rating/:financialSecurityId"
          exact
        >
          <FinancialSecurityDetails type="RATING" />
        </Route>
        <Route
          path="/dashboard/operators/assignments/:operatorUuid/financial-securities/bank-guarantee/:financialSecurityId"
          exact
        >
          <FinancialSecurityDetails type="BANK_GUARANTEE" />
        </Route>
        <Route
          path="/dashboard/operators/assignments/:operatorUuid/financial-securities/parent-company-guarantee/:financialSecurityId"
          exact
        >
          <FinancialSecurityDetails type="PARENT_COMPANY_GUARANTEE" />
        </Route>

        <Route
          path="/dashboard/operators/assignments/:operatorUuid/financial-securities/:financialSecurityType(bank-guarantee|parent-company-guarantee|rating)/:financialSecurityId/request-release"
          exact
        >
          <RequestRelease />
        </Route>

        <Route path="/dashboard/operators/assignments/:operatorUuid/contract/:contractType(fmct|fmct-offer|dect|dect-offer|lect)/:contractId/details">
          <ContractDetails />
        </Route>

        <Route path="/dashboard/operators/assignments/:operatorUuid/submit-financial-security">
          <SubmitFinancialSecurity />
        </Route>

        <Route path="/dashboard/operators/assignments/:operatorUuid/users/:userUuid">
          <AssigmentDetails />
        </Route>

        <Route path="/operators/assignments/apply-for-assignment">
          <ApplyForAssignment />
        </Route>

        <Route path="/operators/assignments/details/tso/:orgId/user/:userId">
          <RedirectTsoDetail />
        </Route>

        <Route path="/operators/assignments/operators/:operatorId/contract">
          <RedirectContractDetail />
        </Route>

        <Route path="/operators/assignments/operators/:operatorUuid">
          <RedirectOtherPages />
        </Route>

        <ThrowNotFound />
      </Switch>
    </RoleGateway>
  );
};

const RedirectTsoDetail: FC = () => {
  const { orgId, userId } = useParams<{ orgId: string; userId: string }>();
  const { pathname, search, state } = useLocation();
  const tso = useTso(orgId);
  const user = useShipperUserData(userId);

  if (!tso.response || !user.response) return <PageSpinner />;
  return (
    <Redirect
      to={{
        pathname: pathname.replace(
          `/operators/assignments/details/tso/${orgId}/user/${userId}`,
          `/dashboard/operators/assignments/${tso.response.data.organizationId}/users/${user.response.data.userId}`
        ),
        search,
        state,
      }}
    />
  );
};

const RedirectContractDetail: FC = () => {
  const { operatorId } = useParams<{ operatorId: string }>();
  const { pathname, search, state } = useLocation();
  const tso = useTso(operatorId);

  if (!tso.response) return <PageSpinner />;

  return (
    <Redirect
      to={{
        pathname: pathname.replace(
          `/operators/assignments/operators/${operatorId}`,
          `/dashboard/operators/assignments/${tso.response.data.organizationId}`
        ),
        search,
        state,
      }}
    />
  );
};

// this redirect was technically not strictly needed as it was already using uuids,
// but we wanted to align all sub-pages of an operator
const RedirectOtherPages: FC = () => {
  const { operatorUuid } = useParams<{ operatorUuid: string }>();
  const { pathname, search, state } = useLocation();
  return (
    <Redirect
      to={{
        pathname: pathname.replace(
          `/operators/assignments/operators/${operatorUuid}`,
          `/dashboard/operators/assignments/${operatorUuid}`
        ),
        search,
        state,
      }}
    />
  );
};
